import { getSourcesQueryKeyPrefix } from '#pplx/frontend/apps/ask/queries/query-keys/sourcesKeys.js';
/**
 * Type guard to verify that query data is an infinite query structure.
 * Checks for the presence of a pages array typical of infinite queries.
 */
function isInfiniteQueryData(value) {
    return (typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        'pages' in value &&
        Array.isArray(value.pages));
}
/**
 * Type guard to verify that a page has a valid sources array.
 */
function isPageWithSources(page) {
    return (page !== null &&
        page !== undefined &&
        typeof page === 'object' &&
        'sources' in page &&
        Array.isArray(page.sources));
}
/**
 * Type guard to verify that an object is a valid ApiSource.
 * Validates essential fields that should be present on all sources from the API.
 */
function isApiSource(value) {
    return (typeof value === 'object' &&
        value !== null &&
        'id' in value &&
        typeof value.id === 'string' &&
        'display_name' in value &&
        typeof value.display_name === 'string' &&
        'description' in value &&
        typeof value.description === 'string' &&
        'type' in value &&
        typeof value.type === 'string');
}
/**
 * Searches the React Query cache for a source by ID.
 *
 * This checks all cached `/rest/sources` queries (including paginated results)
 * to find a matching source, avoiding unnecessary API calls when the source
 * has already been fetched as part of a list query.
 *
 * @param queryClient - The React Query client instance
 * @param sourceId - The source ID to search for
 * @returns The cached source if found, null otherwise
 */
export function getSourceFromCache(queryClient, sourceId) {
    const queryCache = queryClient.getQueryCache();
    const sourcesQueryKeyPrefix = getSourcesQueryKeyPrefix();
    // Get all queries that match the sources prefix
    const validQueries = queryCache
        .findAll({
        queryKey: sourcesQueryKeyPrefix,
        exact: false,
    })
        .filter((query) => !query.state.isInvalidated);
    // Search through all non-invalidated cached sources queries. Reusing an
    // invalidated list row during a detail refetch would resurrect pre-mutation
    // data and make the invalidation self-defeating.
    for (const query of validQueries) {
        const queryData = query.state.data;
        if (!queryData)
            continue;
        // Handle infinite query data (from useApiSources)
        if (isInfiniteQueryData(queryData)) {
            for (const page of queryData.pages) {
                if (isPageWithSources(page)) {
                    const source = page.sources.find((s) => {
                        return isApiSource(s) && s.id === sourceId;
                    });
                    if (source) {
                        return source;
                    }
                }
            }
        }
        // Handle single query data (from useApiSource or other single-source queries)
        if (isApiSource(queryData) && queryData.id === sourceId) {
            return queryData;
        }
    }
    // Grouped responses embed selectable family members inside the primary row;
    // fall back to them only when no top-level cache entry has the requested ID.
    for (const query of validQueries) {
        const queryData = query.state.data;
        if (!queryData || !isInfiniteQueryData(queryData))
            continue;
        for (const page of queryData.pages) {
            if (isPageWithSources(page)) {
                const familyMember = findFamilyMemberById(page.sources, sourceId);
                if (familyMember) {
                    return familyMember;
                }
            }
        }
    }
    return null;
}
function findFamilyMemberById(sources, sourceId) {
    for (const source of sources) {
        if (!isApiSource(source)) {
            continue;
        }
        const familyMember = source.family_info?.members?.find((member) => isApiSource(member) && member.id === sourceId);
        if (familyMember) {
            return familyMember;
        }
    }
    return undefined;
}
//# sourceMappingURL=getSourceFromCache.js.map